import { useState } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  useTheme,
  Divider,
  IconButton,
} from "@mui/material";

import { darkColors } from "../../../theme/colors";
import englishTranslations from "../../../i18n/translations/en";
import PlanetXDescription from "../../utils/footer-text";
import SocialAccountBar from "../../utils/social-account-bar";
import GradientButton from "../../utils/gradient-button";
import SocialIconsWithText from "../../utils/social-icons";
import FieldLabel from "../../utils/field-label";
import FieldInput from "../../utils/field-input";
import Logo from "../../utils/logo";
import Headings from "../../utils/headings";
import useWindowHeight from "../../../hooks/window-size/use-window-height";
import useWindowWidth from "../../../hooks/window-size/use-window-width";
import HeroImage from "../../utils/hero-image";
import BackgroundContainer from "../../utils/background-container";
import ConnectWallet from "../signup/connect-wallet";
import { useNavigate } from "react-router-dom";
import routes from "../../../constants/routes";
import apiRoutes from "../../../api/routes";
import useApiRequest from "../../../api/api";
import { useSnackbar } from "../../utils/SnackbarProvider";
import LoadingBackdrop from "../../utils/loader";
import { useGoogleAuth } from "../../utils/AuthServices";
import { useUser } from "../../../context/user-context";
import Popup from "../../utils/popups/otp-popup";
import Otp from "../otp";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import TermsPrivacyLinks from "../../utils/terms-privacy-links";

// Assume these image paths are correct for your project structure

export default function SignInForm() {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar(); // Use the global Snackbar
  const [isLoading, setIsLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility

  const { handleGoogleLogin } = useGoogleAuth({
    isLoading: setIsLoading,
  });
  const { user, updateUser } = useUser();

  const { makeApiCall } = useApiRequest();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 1050;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  const handleIconClick = (url) => {
    window.open(url, "_blank");
    // Add additional logic for each icon as needed
  };

  const handleTextClick = () => {
    navigate(routes.register);

    // Add logic for text click
  };
  const handleLoginClick = async () => {
    setIsLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        userTypeID: 2,
      };
      const response = await makeApiCall(apiRoutes.auth.login, "POST", payload);
      if (response.success) {
        if (response.data) {
          localStorage.setItem("user", JSON.stringify(response.data));
          updateUser(response.data);
          navigate(routes.home);
        } else {
          setShowOTP(true);
        }
      } else {
        showSnackbar(response.message, "error");
      }

      // Show the ConnectWallet component by updating state
    } catch (e) {
      showSnackbar(e, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialIconClick = (platform) => {
    if (platform === "google") {
      handleGoogleLogin();
    }
    // Add additional logic based on the platform clicked
  };
  const handleContinue = (data) => {
    localStorage.setItem("user", JSON.stringify(data));
    updateUser(data);
    navigate(routes.home);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  return (
    <BackgroundContainer>
      <LoadingBackdrop isLoading={isLoading} />

      <Container maxWidth={false} sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            minHeight: "100vh",
            gap: 4,
            py: 4,
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              flex: 1,
              maxWidth: isMobile ? "100%" : "30%",
              ml: "6%",
              pr: isMobile ? 4 : 0,
              py: 4,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between", // This spreads out the content
            }}
          >
            <Box>
              <Logo />

              {/* Sign Up Form */}
              <Headings
                heading={englishTranslations.auth.loginHeading}
                subHeading={englishTranslations.auth.loginSubHeading}
              />

              <Stack>
                <FieldLabel text={englishTranslations.auth.email} />
                <FieldInput
                  label="mail@here.com"
                  name="email"
                  value={formData.username}
                  onChange={handleChange}
                  type="text" // You can also use "password", "email", etc.
                />

                <FieldLabel text={englishTranslations.auth.password} />
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FieldInput
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    type={showPassword ? "text" : "password"} // Toggle between text and password
                  />
                  <IconButton
                    onClick={togglePasswordVisibility}
                    className="white-icon"
                    sx={{ position: "absolute", right: 8 }}
                  >
                    {showPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: isMobile ? "flex-col" : "flex",
                    alignItems: "center",
                    gap: 2,
                    mt: 4,
                  }}
                >
                  <GradientButton
                    text={englishTranslations.auth.loginHeading}
                    onClick={handleLoginClick}
                    width={isMobile ? "100%" : "auto"}
                  />

                  {!isMobile && (
                    <Typography
                      variant="body2"
                      sx={{
                        border: "1px solid",
                        borderLeft: "1px",
                        height: 50,
                        ml: 1,
                      }}
                    />
                  )}
                  {isMobile && (
                    <Divider sx={{ my: 3, background: darkColors.gray }} />
                  )}

                  <SocialIconsWithText
                    text={englishTranslations.auth.loginWith}
                    onIconClick={handleSocialIconClick}
                    isMobile={isMobile}
                  />
                </Box>
              </Stack>
            </Box>
            <Box sx={{ mt: "auto", pt: isMobile ? 0 : 4 }}>
              <SocialAccountBar
                text={englishTranslations.auth.noAccount}
                onIconClick={handleIconClick}
                onTextClick={handleTextClick}
                isMobile={isMobile}
              />
              <Divider sx={{ my: 2, background: darkColors.gray }} />
              <PlanetXDescription isMobile={isMobile} />
              <TermsPrivacyLinks />
            </Box>
          </Box>

          {/* Right Section - Hero Image */}

          {/* <HeroImage /> */}
        </Box>
      </Container>
      <Popup open={showOTP} onClose={() => setShowOTP(false)}>
        <Otp
          handleBack={() => {
            setShowOTP(false);
          }}
          handleContinue={handleContinue}
          email={formData.email}
        />
      </Popup>
    </BackgroundContainer>
  );
}
